import { useState } from 'react';

import {
  OneSchemaLaunchError as BaseOneSchemaLaunchError,
  OneSchemaLaunchParams as BaseOneSchemaLaunchParams,
  OneSchemaLaunchStatus as BaseOneSchemaLaunchStatus,
} from '@oneschema/importer';
// eslint-disable-next-line no-restricted-imports
import BaseOneSchemaImporter, {
  OneSchemaImporterProps as BaseOneSchemaImporterProps,
} from '@oneschema/react';
import * as Sentry from '@sentry/browser';
import invariant from 'invariant';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import { isProduction } from '@watershed/config/env';
import { getPublicRuntimeConfig } from '@watershed/config/utils/publicRuntimeConfig';
import { Flags } from '@watershed/constants/flags';
import { SupportedLocale } from '@watershed/intl/constants';
import useLocale from '@watershed/intl/frontend/useLocale';

import { useFeatureFlag } from '@watershed/app-dashboard/utils/FeatureFlag';

export enum OneSchemaImporterErrorType {
  FileUploadFailed = 'FileUploadFailed',
  ImportError = 'ImportError',
  InitializationError = 'InitializationError',
  LaunchError = 'LaunchError',
  ValidationWebhookError = 'ValidationWebhookError',
  CodeHookError = 'CodeHookError',
}

/**
 * Pulled from [OneSchema docs](https://docs.oneschema.co/docs/translate-importer-into-different-language#supported-languages)
 */
const _ONE_SCHEMA_LANGUAGE_CODES = [
  'en',
  'ar',
  'zh',
  'cs',
  'da',
  'nl',
  'fi',
  'fr',
  'de',
  'hu',
  'it',
  'jp',
  'ko',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'es',
  'sv',
] as const;
type OneSchemaLanguageCode = (typeof _ONE_SCHEMA_LANGUAGE_CODES)[number];

export type OnLaunchedArgs = Omit<
  BaseOneSchemaLaunchStatus,
  'error' | 'success'
> & {
  embedId: NonNullable<BaseOneSchemaLaunchStatus['embedId']>;
  sessionToken: NonNullable<BaseOneSchemaLaunchStatus['sessionToken']>;
};

/**
 * We want to clean up the abstraction for `onLaunched` and `onError`:
 * - `onLaunched` should only be run upon a succcessful launch event.
 *    -  However, on the base importer, onLaunched runs on both successful and failed launch events.
 * - `onError` should handle all types of errors, i.e. at launch and import time.
 *    - However, on the base importer, `onError` is only run on errors at _import_ time.
 *
 * To perform this override, we need to omit the `onLaunched` and `onError` keys. However, BaseOneSchemaImporterProps
 * contains a union type and [the `Omit` utility does not distribute over union types](https://stackoverflow.com/a/67794430).
 *
 * To distribute the `Omit` utility across each union constituent, we must use the
 * [distributive conditional type shown below](https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OverrideEventHandlers<ImporterProps> = ImporterProps extends any
  ? Omit<ImporterProps, 'onLaunched' | 'onError'> & {
      // The onLaunched handler should only run on successful launches
      onLaunched?: (args: OnLaunchedArgs) => void;
      onError?: (error: string, errorType: OneSchemaImporterErrorType) => void;
      errorOpts?: { notifySentry: boolean };
    }
  : never;

type OneSchemaImporterProps =
  OverrideEventHandlers<BaseOneSchemaImporterProps> &
    Pick<BaseOneSchemaLaunchParams, 'customizationOverrides'>;

function extractKeysToLog(args: Readonly<BaseOneSchemaImporterProps>) {
  return {
    templateKey: 'templateKey' in args ? args.templateKey : null,
    sessionToken: 'sessionToken' in args ? args.sessionToken : null,
    templateGroupKey: 'templateGroupKey' in args ? args.templateGroupKey : null,
  };
}

const LOCALE_TO_LANG_CODE: {
  [locale in SupportedLocale]: OneSchemaLanguageCode;
} = {
  'de-DE': 'de',
  'en-US': 'en',
  'en-GB': 'en',
  'en-PL': 'es', // OneSchema does not offer a pseudo-locale language code so we'll default to Spanish
  'fr-FR': 'fr',
  'ja-JP': 'jp',
  'pt-BR': 'pt',
  'es-ES': 'es',
  'pl-PL': 'pl',
  'tr-TR': 'en', // OneSchema does not offer a Turkish language code so we'll default to English
  'zh-CN': 'zh',
  'it-IT': 'it',
  'id-ID': 'en', // OneSchema does not offer an Indonesian language code so we'll default to English
};

/**
 * A wrapper component for the OneSchema's React Importer component
 * Adds basic error handling and analytics to the component
 * @param onLaunched callback when the Importer has successfully launched.
 * @param onError callback when the Importer hits an error.
 */
export default function OneSchemaImporter({
  onLaunched,
  onError,
  onCancel,
  onSuccess,
  errorOpts = { notifySentry: true },
  customizationOverrides,
  ...params
}: OneSchemaImporterProps) {
  const [embedId, setEmbedId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(
    'sessionToken' in params ? params.sessionToken : null
  );
  const locale = useLocale();
  const languageCode = LOCALE_TO_LANG_CODE[locale];

  const disableOneSchemaAI = useFeatureFlag(
    Flags.DataIngestionDisableOneSchemaAi
  );
  const adjustedCustomizationOverrides = disableOneSchemaAI
    ? { ...customizationOverrides, aiSuggestedMappings: [] }
    : customizationOverrides;

  const loggingKeys = { ...extractKeysToLog(params), embedId, sessionToken };
  return (
    <BaseOneSchemaImporter
      languageCode={languageCode}
      devMode={!isProduction() || !!navigator.webdriver}
      baseUrl={`https://embed.${getPublicRuntimeConfig('NEXT_PUBLIC_ONE_SCHEMA_DOMAIN')}`}
      customizationOverrides={adjustedCustomizationOverrides}
      {...params}
      onCancel={() => {
        Analytics.action('OneSchemaImporter-cancel', loggingKeys);
        onCancel?.();
      }}
      onSuccess={(result) => {
        Analytics.action('OneSchemaImporter-success', loggingKeys);
        onSuccess?.(result);
      }}
      onLaunched={(result) => {
        const { success, error, embedId, sessionToken } = result;
        if (success) {
          invariant(
            embedId && sessionToken,
            'Missing session token or embed ID',
            { data: { embedId, sessionToken } }
          );
          Analytics.action('OneSchemaImporter-launched', {
            ...loggingKeys,
            embedId,
            sessionToken,
          });
          setEmbedId(embedId);
          setSessionToken(sessionToken);
          onLaunched?.({ ...result, embedId, sessionToken });
        }

        if (error) {
          Analytics.error('OneSchemaImporter-error', {
            ...loggingKeys,
            error,
            type: OneSchemaImporterErrorType.LaunchError,
            embedId,
            sessionToken,
          });
          if (errorOpts.notifySentry) {
            Sentry.captureException(
              new Error(`OneSchema importer failed to launch - ${error}`),
              { tags: { ...loggingKeys, embedId, sessionToken } }
            );
          }
          // Use OneSchema's launch error enum key as the error string. The output string should be one of
          // "MissingTemplate", "MissingJwt", "MissingSessionToken", or "MissingTemplateGroup"
          const launchErrorDetails = BaseOneSchemaLaunchError[error];
          onError?.(launchErrorDetails, OneSchemaImporterErrorType.LaunchError);
        }
      }}
      onError={(error) => {
        const type = error.message.startsWith('Validation webhook failed')
          ? OneSchemaImporterErrorType.ValidationWebhookError
          : error.message.startsWith('File upload failed')
            ? OneSchemaImporterErrorType.FileUploadFailed
            : error.message.includes('failed to respond for initialization')
              ? OneSchemaImporterErrorType.InitializationError
              : error.message.includes('Code hook')
                ? OneSchemaImporterErrorType.CodeHookError
                : OneSchemaImporterErrorType.ImportError;

        Analytics.error('OneSchemaImporter-error', {
          ...loggingKeys,
          error,
          type,
          embedId,
          sessionToken,
        });
        if (errorOpts.notifySentry) {
          Sentry.captureException(
            new Error(
              `OneSchema importer failure - ${error.message} - ${type}`
            ),
            { tags: { ...loggingKeys, embedId, sessionToken, type } }
          );
        }
        onError?.(error.message, type);
      }}
    />
  );
}
